import React, { useState, useContext, useEffect, useRef } from "react"
import { CalculationResult, CalculationState } from "../model/CalculationResult"
import { SearchLocation, SearchLocationType } from "../model/SearchLocation"
import { CalculationParams, CarType, EngineType } from "../model/CalculationParams"
import { LastSearchedItem } from "../model/LastSearchedItem"
import { Ticket } from "../model/Ticket"
import { Equivalence, EquivalenceMainType, EquivalenceTypes } from "../model/Equivalence"
import { EquivalenceCardType } from "../modules/equivalenceCard/EquivalenceCard"
import { Convert, CostConsumptionDataInterface } from "../model/CostAndConsumptionData"

import costAndConsumptionBackupJSON from "../../data/costAndConsumptionBackup.json"

const API_BASE_URL = "https://efastatic.vvs.de/umweltrechner/"

export enum Screens {
  landing = "landing",
  locationSelect = "locationSelect",
  result = "result"
}

export enum PopUpStates {
  travelFrequency = "travelFrequency",
  totalCost = "totalCost",
  emission = "emission",
  carSelection = "carSelection",
  share = "share"
}

export var OPERATION_COST_MAP = new Map<CarType, Map<EngineType, number>>()
export var OTHER_COST_MAP = new Map<CarType, Map<EngineType, number>>()
export var COST_INFO_TEXT = ""
export var PUBLIC_CONSUMPTION_MAP = new Map<String, number>()
export var CAR_CONSUMPTION_MAP = new Map<CarType, Map<EngineType, number>>()

const LAST_SEARCH_ITEMS_KEY = "lastSearches"
const LAST_SEARCH_LOCATIONS_KEY = "lastSearchedLocation"

const LEISURE_WEEK_COUNT = 44

function getCarTypeName(carType: CarType): string {
  switch (carType) {
    case CarType.small:
      return "Kleinwagen"
    case CarType.medium:
      return "Mittelklasse"
    case CarType.suv:
      return "Obere Mittelklasse"
  }
}

function fillCostConsumptionMaps(costAndConsumptionValues: CostConsumptionDataInterface) {
  OPERATION_COST_MAP.set(
    CarType.small,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["operationCost"]["small"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["operationCost"]["small"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["operationCost"]["small"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["operationCost"]["small"]["electric"]]
    ])
  )

  OPERATION_COST_MAP.set(
    CarType.medium,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["operationCost"]["medium"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["operationCost"]["medium"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["operationCost"]["medium"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["operationCost"]["medium"]["electric"]]
    ])
  )

  OPERATION_COST_MAP.set(
    CarType.suv,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["operationCost"]["upperMedium"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["operationCost"]["upperMedium"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["operationCost"]["upperMedium"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["operationCost"]["upperMedium"]["electric"]]
    ])
  )

  OTHER_COST_MAP.set(
    CarType.small,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["otherCost"]["small"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["otherCost"]["small"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["otherCost"]["small"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["otherCost"]["small"]["electric"]]
    ])
  )

  OTHER_COST_MAP.set(
    CarType.medium,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["otherCost"]["medium"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["otherCost"]["medium"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["otherCost"]["medium"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["otherCost"]["medium"]["electric"]]
    ])
  )

  OTHER_COST_MAP.set(
    CarType.suv,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["otherCost"]["upperMedium"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["otherCost"]["upperMedium"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["otherCost"]["upperMedium"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["otherCost"]["upperMedium"]["electric"]]
    ])
  )

  PUBLIC_CONSUMPTION_MAP.set("S-Bahn", costAndConsumptionValues["publicTransportConsumption"]["sbahn"])
  PUBLIC_CONSUMPTION_MAP.set("Bus", costAndConsumptionValues["publicTransportConsumption"]["bus"])
  PUBLIC_CONSUMPTION_MAP.set("R-Bahn", costAndConsumptionValues["publicTransportConsumption"]["rbahn"])
  PUBLIC_CONSUMPTION_MAP.set("Stadtbahn", costAndConsumptionValues["publicTransportConsumption"]["stadtbahn"])
  PUBLIC_CONSUMPTION_MAP.set("Zahnradbahn", costAndConsumptionValues["publicTransportConsumption"]["zahnradbahn"])

  COST_INFO_TEXT = costAndConsumptionValues["costInfoText"]

  CAR_CONSUMPTION_MAP.set(
    CarType.small,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["carConsumption"]["small"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["carConsumption"]["small"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["carConsumption"]["small"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["carConsumption"]["small"]["electric"]]
    ])
  )

  CAR_CONSUMPTION_MAP.set(
    CarType.medium,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["carConsumption"]["medium"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["carConsumption"]["medium"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["carConsumption"]["medium"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["carConsumption"]["medium"]["electric"]]
    ])
  )

  CAR_CONSUMPTION_MAP.set(
    CarType.suv,
    new Map<EngineType, number>([
      [EngineType.benzin, costAndConsumptionValues["carConsumption"]["upperMedium"]["benzin"]],
      [EngineType.diesel, costAndConsumptionValues["carConsumption"]["upperMedium"]["diesel"]],
      [EngineType.hybrid, costAndConsumptionValues["carConsumption"]["upperMedium"]["hybrid"]],
      [EngineType.electric, costAndConsumptionValues["carConsumption"]["upperMedium"]["electric"]]
    ])
  )
}

function getCostAndConsumptionValues() {
  fetch("/costAndConsumption.json")
    .then(function (response) {
      return response.json()
    })
    .then(function (costJSON: JSON) {
      try {
        const costAndConsumptionValues = Convert.toCostConsumptionDataInterface(JSON.stringify(costJSON))

        fillCostConsumptionMaps(costAndConsumptionValues)
      } catch (error) {
        console.log("CostConsumption Data Error Rollback to Backup")

        fillCostConsumptionMaps(Convert.toCostConsumptionDataInterface(JSON.stringify(costAndConsumptionBackupJSON)))
      }
    })
    .catch(function () {
      console.log("CostConsumption Fetch Data Error Rollback to Backup")
      fillCostConsumptionMaps(Convert.toCostConsumptionDataInterface(JSON.stringify(costAndConsumptionBackupJSON)))
    })
}

var userId = 0

type IState = {
  pageNumber: number
  currentScreen: Screens
  locationSearchText: string
  searchLocations: SearchLocation[]
  isLandingPageForOrigin: boolean
  originLocation: SearchLocation | null
  destinationLocation: SearchLocation | null
  calculationResult: CalculationResult
  calculationParams: CalculationParams
  lastSearchedItems: LastSearchedItem[]
  lastSearchedLocations: SearchLocation[]
  carTypeName: string
  isPopUpVisible: boolean
  currentPopUpState: PopUpStates
  selectedTicket: number
  isMobile: boolean
}

const initialState: IState = {
  pageNumber: 0,
  currentScreen: Screens.landing,
  locationSearchText: "",
  searchLocations: [],
  isLandingPageForOrigin: false,
  originLocation: null,
  destinationLocation: null,
  calculationResult: new CalculationResult(
    0,
    0,
    0,
    0,
    0,
    0,
    [],
    [],
    CalculationState.loading,
    [],
    [],
    new Map<String, number>(),
    false
  ),
  calculationParams: new CalculationParams(220, 1, CarType.medium, EngineType.benzin),
  lastSearchedItems: [],
  lastSearchedLocations: [],
  carTypeName: getCarTypeName(CarType.medium),
  isPopUpVisible: false,
  currentPopUpState: PopUpStates.emission,
  selectedTicket: 0,
  isMobile: false
}

export type IDataStore = IState & {
  setPageNumber: (number: number) => void
  setCurrentScreen: (screen: Screens) => void
  setLocationSearchText: (text: string) => void
  searchLocation: (searchInput: string, isCoord: boolean) => void
  openLocationSearchPage: (isOrigin: boolean) => void
  setLocation: (location: SearchLocation | null, isOrigin: boolean) => void
  calculateCostAndConsumption: () => void
  saveSearch: () => void
  setCalculationsParams: (params: CalculationParams) => void
  saveSearchLocation: (searchLocation: SearchLocation) => void
  setPopUpVisibility: (isPopUpVisible: boolean) => void
  setPopUpState: (popUpState: PopUpStates) => void
  updateTravelFrequency: (workingDaysInWeek: number, leisureTime: number) => void
  updateCostAndConsumption: () => void
  setSelectedTicket: (selectedTicket: number) => void
  setCarAndEngineType: (carType: CarType, engineType: EngineType) => void
  updateSavedSearch: () => void
  refreshEquivalences: (type: EquivalenceCardType) => void
  getCarConsumptionFactor: () => number
  getCarOperationalCostFactor: () => number
  getCarOtherCostFactor: () => number
  getCarOperationalCost: () => number
  getCarOtherCost: () => number
  openLandingPage: (isOrigin: boolean) => void
  acceptCookie: () => void
  rejectCookie: () => void
  isCookiesAccepted: () => boolean
}

function createStore() {
  const Context = React.createContext<IDataStore>({
    ...initialState,
    setPageNumber: () => undefined,
    setCurrentScreen: () => undefined,
    setLocationSearchText: () => undefined,
    searchLocation: () => undefined,
    openLocationSearchPage: () => undefined,
    setLocation: () => undefined,
    calculateCostAndConsumption: () => undefined,
    saveSearch: () => undefined,
    setCalculationsParams: () => undefined,
    saveSearchLocation: () => undefined,
    setPopUpVisibility: () => undefined,
    setPopUpState: () => undefined,
    updateTravelFrequency: () => undefined,
    updateCostAndConsumption: () => undefined,
    setSelectedTicket: () => undefined,
    setCarAndEngineType: () => undefined,
    updateSavedSearch: () => undefined,
    refreshEquivalences: () => undefined,
    getCarConsumptionFactor: () => 0,
    getCarOperationalCostFactor: () => 0,
    getCarOtherCostFactor: () => 0,
    getCarOperationalCost: () => 0,
    getCarOtherCost: () => 0,
    openLandingPage: () => undefined,
    acceptCookie: () => undefined,
    rejectCookie: () => undefined,
    isCookiesAccepted: () => false
  })

  function Provider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<IState>(initialState)
    // const proxyurl = "https://gr-proxy-server.herokuapp.com/"
    // const proxyurl = "http://localhost:8080/"
    const proxyurl = ""

    const controller = new AbortController()
    const { signal } = controller
    const controllerRef = useRef<AbortController | null>()

    function handleWindowSizeChange() {
      setState((prevState) => ({
        ...prevState,
        isMobile: window.innerWidth < 768
      }))
    }

    useEffect(() => {
      window.addEventListener("popstate", (event) => {
        setState((prevState) => ({
          ...prevState,
          currentScreen: event.state ?? Screens.landing
        }))
      })

      window.addEventListener("resize", handleWindowSizeChange)

      var searchItemsString = localStorage.getItem(LAST_SEARCH_ITEMS_KEY) ?? ""
      var searchItems: LastSearchedItem[] = new Array()

      if (searchItemsString !== "") {
        searchItems = JSON.parse(searchItemsString)
      }

      var searchLocationsString = localStorage.getItem(LAST_SEARCH_LOCATIONS_KEY) ?? ""
      var searchLocations: SearchLocation[] = new Array()

      if (searchLocationsString !== "") {
        searchLocations = JSON.parse(searchLocationsString)
      }

      userId = Date.now()

      setState((prevState) => ({
        ...prevState,
        lastSearchedItems: searchItems,
        lastSearchedLocations: searchLocations,
        isMobile: window.innerWidth < 768
      }))

      getCostAndConsumptionValues()
    }, [])

    const contextObject: IDataStore = {
      ...state,
      rejectCookie() {
        document.cookie = "vvsOptIn=1"
      },
      acceptCookie() {
        document.cookie = "vvsOptIn=7"
      },
      openLandingPage(isOrigin: boolean) {
        setState((prevState) => ({
          ...prevState,
          currentScreen: Screens.landing,
          isLandingPageForOrigin: isOrigin
        }))
      },
      getCarConsumptionFactor(): number {
        return CAR_CONSUMPTION_MAP.get(state.calculationParams.carType)?.get(state.calculationParams.engineType) ?? 0
      },

      getCarOperationalCostFactor(): number {
        return OPERATION_COST_MAP.get(state.calculationParams.carType)?.get(state.calculationParams.engineType) ?? 0
      },

      getCarOtherCostFactor(): number {
        return OTHER_COST_MAP.get(state.calculationParams.carType)?.get(state.calculationParams.engineType) ?? 0
      },

      getCarOperationalCost(): number {
        var yearlyDistance = getYearlyDistance(state.calculationResult.carDistance * 1000)
        return yearlyDistance * this.getCarOperationalCostFactor()
      },

      getCarOtherCost(): number {
        var yearlyDistance = getYearlyDistance(state.calculationResult.carDistance * 1000)
        if (state.calculationParams.engineType === EngineType.electric) {
          return yearlyDistance * this.getCarOtherCostFactor() - LEISURE_WEEK_COUNT * 4.5
        } else return yearlyDistance * this.getCarOtherCostFactor()
      },

      refreshEquivalences(type: EquivalenceCardType) {
        var possibleEquivalences = Equivalence.getEquivalenceTypesForMainType(
          type === EquivalenceCardType.co2 ? EquivalenceMainType.co2 : EquivalenceMainType.cost
        )

        var value =
          type === EquivalenceCardType.co2 ? state.calculationResult.co2Save * 1000 : state.calculationResult.costSave

        possibleEquivalences = possibleEquivalences.filter((equivalenceType) => {
          var equivalenceValue = Equivalence.equivalenceValueMap.get(equivalenceType)
          if (equivalenceValue !== undefined) {
            return equivalenceValue < value
          } else {
            return false
          }
        })

        possibleEquivalences = shuffleArray(possibleEquivalences)

        var firstEquivalenceType: EquivalenceTypes = possibleEquivalences[0]

        var firstValue = Equivalence.equivalenceValueMap.get(firstEquivalenceType) ?? 1
        var firstComparisonValue = Math.round(value / firstValue)
        var firstEquivalence = new Equivalence(firstEquivalenceType, firstComparisonValue)

        possibleEquivalences = possibleEquivalences.filter((obj) => obj !== firstEquivalenceType)
        var secondEquivalenceType = possibleEquivalences[1]

        var secondValue = Equivalence.equivalenceValueMap.get(secondEquivalenceType) ?? 1
        var secondComparisonValue = Math.round(value / secondValue)
        var secondEquivalence = new Equivalence(secondEquivalenceType, secondComparisonValue)

        setState((prevState) => {
          var calculationResult = prevState.calculationResult

          if (type === EquivalenceCardType.co2) {
            calculationResult.co2Equivalences = [firstEquivalence, secondEquivalence]
          } else {
            calculationResult.costEquivalences = [firstEquivalence, secondEquivalence]
          }

          return {
            ...prevState,
            calculationResult: calculationResult
          }
        })
      },
      setCarAndEngineType(carType: CarType, engineType: EngineType) {
        setState((prevState) => {
          var calculationParams = prevState.calculationParams
          calculationParams.carType = carType
          calculationParams.engineType = engineType

          var calculationResult = prevState.calculationResult
          var carConsumption = calculateCarConsumption(calculationResult.carDistance * 1000)
          var carCost = calculateCarCost(calculationResult.carDistance * 1000)

          var tickets = getSuitableTickets(calculationResult.allTickets)
          var vvsCost = 0
          if (tickets.length > 0) {
            vvsCost = getTicketPrice(tickets[0])
          }

          calculationResult.carCO2Consumption = carConsumption
          calculationResult.carCost = carCost
          calculationResult.tickets = tickets
          calculationResult.vvsCost = vvsCost
          calculationResult.co2Save = carConsumption - calculationResult.vvsCO2Consumption
          calculationResult.costSave = carCost - calculationResult.vvsCost

          calculationResult.co2Equivalences = findEquivalences(
            EquivalenceMainType.co2,
            calculationResult.co2Save * 1000
          )
          calculationResult.costEquivalences = findEquivalences(EquivalenceMainType.cost, calculationResult.costSave)
          return {
            ...prevState,
            calculationParams: calculationParams,
            calculationResult: calculationResult,
            carTypeName: getCarTypeName(carType),
            selectedTicket: 0
          }
        })
      },
      setSelectedTicket(selectedTicket: number) {
        setState((prevState) => {
          var calculationResult = prevState.calculationResult
          if (calculationResult.tickets.length > selectedTicket) {
            var ticket = calculationResult.tickets[selectedTicket]
            calculationResult.vvsCost = getTicketPrice(ticket)
            calculationResult.costSave = calculationResult.carCost - calculationResult.vvsCost
            calculationResult.costEquivalences = findEquivalences(EquivalenceMainType.cost, calculationResult.costSave)
          }

          return {
            ...prevState,
            calculationResult: calculationResult,
            selectedTicket: selectedTicket
          }
        })
      },
      updateCostAndConsumption() {
        this.calculateCostAndConsumption()
      },
      updateTravelFrequency(workingDaysInWeek: number, leisureTime: number) {
        setState((prevState) => {
          var calculationParams = prevState.calculationParams

          calculationParams.workingDays = workingDaysInWeek * 44
          calculationParams.leisureTime = leisureTime

          return {
            ...prevState,
            calculationParams: calculationParams
          }
        })
      },
      setCalculationsParams(params: CalculationParams) {
        setState((prevState) => ({
          ...prevState,
          calculationParams: params,
          carTypeName: getCarTypeName(params.carType)
        }))
      },
      saveSearchLocation(searchLocation: SearchLocation) {
        var i = 0
        while (i < this.lastSearchedLocations.length) {
          if (this.lastSearchedLocations[i].localityRef === searchLocation.localityRef) {
            this.lastSearchedLocations.splice(i, 1)
          } else {
            ++i
          }
        }

        this.lastSearchedLocations.splice(0, 0, searchLocation)
        this.lastSearchedLocations = this.lastSearchedLocations.slice(0, 10)

        var saveString = JSON.stringify(this.lastSearchedLocations)

        localStorage.setItem(LAST_SEARCH_LOCATIONS_KEY, saveString)
      },
      updateSavedSearch() {
        this.lastSearchedItems.shift()
        this.saveSearch()
      },
      saveSearch() {
        if (this.originLocation !== null && this.destinationLocation !== null) {
          var searchItem = new LastSearchedItem(this.originLocation, this.destinationLocation, this.calculationParams)
          this.lastSearchedItems.splice(0, 0, searchItem)
          this.lastSearchedItems = this.lastSearchedItems.slice(0, 5)

          var saveString = JSON.stringify(this.lastSearchedItems)

          localStorage.setItem(LAST_SEARCH_ITEMS_KEY, saveString)

          setState((prevState) => ({
            ...prevState,
            lastSearchedItems: this.lastSearchedItems
          }))
        }
      },
      setPageNumber: (_number) => {
        setState((prevState) => ({
          ...prevState,
          pageNumber: _number
        }))
      },
      setCurrentScreen: (screen) => {
        if (window._paq !== undefined && contextObject.isCookiesAccepted()) {
          window._paq.push(["setDocumentTitle", screen])
          window._paq.push(["trackPageView"])
        }
        setState((prevState) => {
          window.history.pushState(prevState.currentScreen, "", null)
          return {
            ...prevState,
            currentScreen: screen
          }
        })
      },
      setLocationSearchText: (searchText) => {
        setState((prevState) => ({
          ...prevState,
          locationSearchText: searchText
        }))
      },
      openLocationSearchPage: (isOrigin) => {
        setState((prevState) => {
          window.history.pushState(prevState.currentScreen, "", null)

          return { ...prevState, currentScreen: Screens.locationSelect, isLandingPageForOrigin: isOrigin }
        })
      },
      searchLocation: (searchInput, isCoord) => {
        if (searchInput === "") {
          setState((prevState) => ({
            ...prevState,
            searchLocations: prevState.lastSearchedLocations
          }))

          return
        } else {
          setState((prevState) => ({
            ...prevState,
            searchLocations: prevState.searchLocations
          }))
        }

        if (controllerRef.current) {
          controllerRef.current.abort()
        }
        const controller = new AbortController()
        controllerRef.current = controller

        var settings = {
          // async: true,
          // crossDomain: true,
          // method: "GET",
          // headers: {
          //   Accept: "*/*",
          //   "Cache-Control": "no-cache",
          //   Host: "efastatic.vvs.de",
          //   Connection: "keep-alive",
          //   "cache-control": "no-cache"
          // },
          signal: controllerRef.current?.signal
        }

        const typeSf = isCoord ? "coord" : "any"

        fetch(
          proxyurl +
            API_BASE_URL +
            "XML_STOPFINDER_REQUEST?outputFormat=rapidJSON&SpEncId=0&coordOutputFormat=EPSG:4326&serverInfo=1&suggestApp=vvs&type_sf=" +
            typeSf +
            "&version=10.2.10.139&name_sf=" +
            searchInput,
          settings
        )
          .then((response) => response.text())
          .then((text) => {
            var jsonResponse = JSON.parse(text)

            var locations = jsonResponse.locations

            var searchLocations: SearchLocation[] = []

            if (Array.isArray(locations)) {
              locations.map((location) => {
                var searchLocation = parseLocation(location)
                searchLocations.push(searchLocation)
              })
            }

            searchLocations = searchLocations.sort(function (locationA, locationB) {
              return locationB.quality - locationA.quality
            })

            setState((prevState) => ({
              ...prevState,
              searchLocations: searchLocations
            }))

            controllerRef.current = null
          })
          .catch(function (error) {})
      },
      setLocation(location, isOrigin) {
        if (isOrigin) {
          setState((prevState) => ({
            ...prevState,
            originLocation: location
          }))
        } else {
          setState((prevState) => ({
            ...prevState,
            destinationLocation: location
          }))
        }
      },
      calculateCostAndConsumption() {
        var result = this.calculationResult
        result.state = CalculationState.loading

        setState((prevState) => ({
          ...prevState,
          calculationResult: result
        }))

        var nextMonday = new Date()
        nextMonday.setDate(nextMonday.getDate() + ((1 + 7 - nextMonday.getDay()) % 7))

        fetch(
          proxyurl +
            API_BASE_URL +
            "XML_TRIP_REQUEST2?language=de&locationServerActive=1&command=&usage=&anyObjFilter=&searchLimitMinutes=360&ptOptionsActive=1&illumTransfer=on&itOptionsActive=1&delMinDistTrips=1&coordListOutputFormat=STRING&convertStopsPTKernel2LocationServer=1&convertPOIsITKernel2LocationServer=1&outputOptionsActive=1&calcNumberOfTrips=1&itdTime=1600&computeMonomodalTripCar=1&speedFactorCar=100%25&costFactorCar=0%25&distanceFactorCar=10%25&traveltimeFactorCar=90%25&lineRestriction=403&calculateCO2=1&calculateDistance=1&anyType_origin=&type_origin=any&anyObjFilter_origin=0&place_origin=&anyType_destination=&type_destination=any&anyObjFilter_destination=0&place_destination=&outputFormat=rapidJSON&includeFares=true&useUT=true&name_origin=" +
            state.originLocation?.localityRef +
            "&name_destination=" +
            state.destinationLocation?.localityRef +
            "&itdDate=" +
            formatDate(nextMonday)
        )
          .then((response) => response.text())
          .then((text) => {
            var jsonResponse = JSON.parse(text)
            var journeys = jsonResponse.journeys
            var systemMessages = jsonResponse.systemMessages

            var isTripError = false

            if (Array.isArray(systemMessages)) {
              systemMessages.map((systemMessage) => {
                if (systemMessage.type !== undefined && systemMessage.code !== undefined) {
                  if (systemMessage.type === "error" && systemMessage.code === 0) {
                    isTripError = true
                  }
                }
              })
            }

            var carDistance = 0
            var vvsDistance = 0
            var vvsJourneyConsumption = 0
            var tickets: Ticket[] = []
            var vvsDistanceMap: Map<String, number> = new Map<String, number>()
            var isWalkingDistance = false

            if (Array.isArray(journeys)) {
              journeys.map((journey) => {
                var isAuto = false
                var tempConsumption = 0
                var tempVVSDistanceMap: Map<String, number> = new Map<String, number>()

                if (journey.legs.length === 1) {
                  var leg = journey.legs[0]
                  if (leg.transportation.product.name === "Auto") {
                    isAuto = true
                  } else if (leg.transportation.product.name === "footpath") {
                    isWalkingDistance = true
                  }
                }

                if (isWalkingDistance) {
                  vvsDistance = journey.legs[0].distance
                } else if (isAuto) {
                  carDistance = journey.legs[0].distance
                } else {
                  vvsDistance = 0

                  if (journey.fare.tickets !== undefined) {
                    tickets = journey.fare.tickets
                  }

                  var legs = journey.legs
                  if (Array.isArray(legs)) {
                    legs.map((leg) => {
                      var properties = leg.properties
                      Object.keys(properties).forEach(function (key) {
                        if (key.includes("CO2Emission_Actual")) {
                          tempConsumption += Number(properties[key])
                        } else if (key.includes("PartialRouteDistance")) {
                          var routeDistance = Number(properties[key])

                          var transportType = leg.transportation.product.name

                          if (transportType !== undefined) {
                            if (tempVVSDistanceMap.has(transportType)) {
                              var distance = tempVVSDistanceMap.get(transportType)
                              tempVVSDistanceMap.set(transportType, Number(properties[key] + distance))
                            } else {
                              tempVVSDistanceMap.set(transportType, Number(properties[key]))
                            }
                          }

                          if (Array.isArray(routeDistance)) {
                            if (leg.distance !== undefined) {
                              vvsDistance += leg.distance
                            }
                          } else {
                            vvsDistance += routeDistance
                          }
                        }
                      })
                    })
                  }
                }

                if (vvsJourneyConsumption == 0 && tempConsumption != 0) {
                  vvsJourneyConsumption = tempConsumption
                  vvsDistanceMap = tempVVSDistanceMap
                }

                if (tempConsumption != 0 && tempConsumption < vvsJourneyConsumption) {
                  vvsJourneyConsumption = tempConsumption
                  vvsDistanceMap = tempVVSDistanceMap
                }
              })
            }

            vvsJourneyConsumption = 0

            vvsDistanceMap.forEach((value, key) => {
              const consumptionValue = PUBLIC_CONSUMPTION_MAP.get(key)

              if (consumptionValue !== undefined) {
                vvsJourneyConsumption += (value / 1000) * consumptionValue
              }
            })

            if (isWalkingDistance) {
              tickets = []
              vvsJourneyConsumption = 0
            }

            var carConsumption = calculateCarConsumption(carDistance)
            var vvsConsumption = calculateYearlyVVSConsumption(vvsJourneyConsumption)

            var carCost = calculateCarCost(carDistance)

            var suitableTickets = getSuitableTickets(tickets)
            var vvsCost = 0
            if (suitableTickets.length > 0) {
              vvsCost = getTicketPrice(suitableTickets[0])
            }

            if (window._paq !== undefined && this.isCookiesAccepted()) {
              window._paq.push([
                "trackEvent",
                "result-page",
                "calculate-cost-consumption",
                "co2-save",
                carConsumption - vvsConsumption
              ])
              window._paq.push([
                "trackEvent",
                "result-page",
                "calculate-cost-consumption",
                "cost-save",
                carCost - vvsCost
              ])
            }

            setState((prevState) => ({
              ...prevState,
              selectedTicket: 0,
              calculationResult: new CalculationResult(
                carDistance / 1000,
                vvsDistance / 1000,
                carConsumption,
                vvsConsumption,
                carCost,
                vvsCost,
                findEquivalences(EquivalenceMainType.co2, (carConsumption - vvsConsumption) * 1000),
                findEquivalences(EquivalenceMainType.cost, carCost - vvsCost),
                isTripError ? CalculationState.error : CalculationState.done,
                suitableTickets,
                tickets,
                vvsDistanceMap,
                isWalkingDistance
              )
            }))
          })
      },
      setPopUpVisibility: (isVisible) => {
        setState((prevState) => ({
          ...prevState,
          isPopUpVisible: isVisible
        }))
      },
      setPopUpState: (popUpState) => {
        setState((prevState) => ({
          ...prevState,
          currentPopUpState: popUpState
        }))
      },
      isCookiesAccepted(): boolean {
        const cookies = document.cookie
        if (cookies !== undefined) {
          const cookieValue = cookies
            ?.split("; ")
            ?.find((row) => row.startsWith("vvsOptIn="))
            ?.split("=")[1]

          if (cookieValue === "7" || cookieValue === "3") {
            return true
          } else {
            return false
          }
        }

        return false
      }
    }

    function getTicketPrice(ticket: Ticket): number {
      var priceMultiplier = 1

      if (ticket.timeValidity === "DAY") {
        priceMultiplier = state.calculationParams.workingDays
      } else if (ticket.timeValidity === "MONTH") {
        priceMultiplier = 12
      } else if (ticket.timeValidity === "MULTIPLE" && ticket.id.includes("05225")) {
        priceMultiplier = Math.ceil(state.calculationParams.workingDays / 11 / 10) * 11
      }

      var leisureCost = state.calculationParams.leisureTime * LEISURE_WEEK_COUNT * 2.55

      if (ticket.timeValidity === "MONTH" || ticket.timeValidity === "YEAR") {
        leisureCost = 0
      }

      var ticketPrice = ticket.priceBrutto * priceMultiplier

      const currentYear = new Date().getFullYear()

      if (currentYear === 2022) {
        var monthlyTicketPrice = ticketPrice / 12
        var currentMonth = new Date().getMonth()

        var nineEuroTicketAvailabiliy = 8 - currentMonth

        console.log("9 Euro Ticket Remaining Month :: ", nineEuroTicketAvailabiliy)

        ticketPrice = monthlyTicketPrice * (12 - nineEuroTicketAvailabiliy) + 9 * nineEuroTicketAvailabiliy
      }

      return ticketPrice + leisureCost
    }

    function getSuitableTickets(tickets: Ticket[]): Ticket[] {
      if (tickets.length === 0) {
        return tickets
      }
      var tagesTicket = tickets.filter((ticket) => ticket.id.includes("05025"))

      var dTicket = tickets.filter((ticket) => ticket.id.includes("160199"))
      var dTicketFirmen = tickets.filter((ticket) => ticket.id.includes("165199"))

      var filteredTickets: Ticket[] = []

      if (dTicket.length > 0) {
        dTicket[0].name = "D-Ticket"
        filteredTickets.push(dTicket[0])
      }

      if (dTicketFirmen.length > 0) {
        dTicketFirmen[0].name = "D-Ticket Job"
        filteredTickets.push(dTicketFirmen[0])
      }

      var dailyTicketPrice = -1

      if (tagesTicket.length > 0) {
        tagesTicket[0].name = "EinzelTagesTicket"
        var priceMultiplier = state.calculationParams.workingDays
        var dailyPrice = tagesTicket[0].priceBrutto * priceMultiplier

        dailyTicketPrice = dailyPrice

        if (dTicket.length > 0) {
          var yearlyPrice = dTicket[0].priceBrutto * 12
          if (dailyPrice < yearlyPrice) {
            filteredTickets.splice(0, 0, tagesTicket[0])
          } else {
            filteredTickets.push(tagesTicket[0])
          }
        } else {
          filteredTickets.push(tagesTicket[0])
        }
      }

      return filteredTickets
    }

    function formatDate(date: Date) {
      var month = "" + (date.getMonth() + 1),
        day = "" + date.getDate(),
        year = date.getFullYear()

      if (month.length < 2) month = "0" + month
      if (day.length < 2) day = "0" + day

      return [year, month, day].join("")
    }

    function calculateCarConsumption(distance: number): number {
      const workingDay = state.calculationParams.workingDays
      const leisureTime = state.calculationParams.leisureTime
      const co2Consumption =
        CAR_CONSUMPTION_MAP.get(state.calculationParams.carType)?.get(state.calculationParams.engineType) ?? 0

      var consumption =
        (((distance / 1000) * 2 * workingDay + leisureTime * 10 * LEISURE_WEEK_COUNT) * co2Consumption) / 1000
      return consumption
    }

    function calculateYearlyVVSConsumption(journeyConsumption: number): number {
      if (journeyConsumption === 0) {
        return 0
      }
      const workingDay = state.calculationParams.workingDays
      const leisureTime = state.calculationParams.leisureTime

      var consumption =
        (journeyConsumption * 2 * workingDay) / 1000 +
        (leisureTime * 10 * LEISURE_WEEK_COUNT * PUBLIC_CONSUMPTION_MAP.get("S-Bahn")!) / 1000
      return consumption
    }

    function calculateCarCost(distance: number): number {
      const operationalCost =
        OPERATION_COST_MAP.get(state.calculationParams.carType)?.get(state.calculationParams.engineType) ?? 0

      const otherCost =
        OTHER_COST_MAP.get(state.calculationParams.carType)?.get(state.calculationParams.engineType) ?? 0

      const yearlyDistance = getYearlyDistance(distance)

      var totalCost = yearlyDistance * operationalCost + yearlyDistance * otherCost

      if (state.calculationParams.engineType === EngineType.electric) {
        totalCost = totalCost - LEISURE_WEEK_COUNT * 4.5
      }

      return totalCost
    }

    function getYearlyDistance(distance: number): number {
      return (
        (distance / 1000) * 2 * state.calculationParams.workingDays +
        state.calculationParams.leisureTime * 10 * LEISURE_WEEK_COUNT
      )
    }

    function findEquivalences(mainType: EquivalenceMainType, value: number): Equivalence[] {
      var possibleEquivalences = Equivalence.getEquivalenceTypesForMainType(mainType)
      possibleEquivalences = possibleEquivalences.filter((equivalenceType) => {
        var equivalenceValue = Equivalence.equivalenceValueMap.get(equivalenceType)
        if (equivalenceValue !== undefined) {
          return equivalenceValue < value
        } else {
          return false
        }
      })
      possibleEquivalences = shuffleArray(possibleEquivalences)

      var firstEquivalenceType: EquivalenceTypes = possibleEquivalences[0]
      for (var i = 0; i < possibleEquivalences.length; i++) {
        var equivalence = possibleEquivalences[i]
        var equivalenceValue = Equivalence.equivalenceValueMap.get(equivalence) ?? 1
        var comparisonValue = Math.round(value / equivalenceValue)

        if (comparisonValue >= 100 && comparisonValue <= 1000) {
          firstEquivalenceType = equivalence
          break
        }
      }

      var firstValue = Equivalence.equivalenceValueMap.get(firstEquivalenceType) ?? 1
      var firstComparisonValue = Math.round(value / firstValue)
      var firstEquivalence = new Equivalence(firstEquivalenceType, firstComparisonValue)

      possibleEquivalences = possibleEquivalences.filter((obj) => obj !== firstEquivalenceType)
      var secondEquivalenceType = possibleEquivalences[Math.floor(Math.random() * possibleEquivalences.length)]

      var secondValue = Equivalence.equivalenceValueMap.get(secondEquivalenceType) ?? 1
      var secondComparisonValue = Math.round(value / secondValue)
      var secondEquivalence = new Equivalence(secondEquivalenceType, secondComparisonValue)

      return [firstEquivalence, secondEquivalence]
    }

    function shuffleArray(array: EquivalenceTypes[]): EquivalenceTypes[] {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[array[i], array[j]] = [array[j], array[i]]
      }

      return array
    }

    function parseLocation(location: any): SearchLocation {
      var name = ""
      var type = SearchLocationType.poi
      var locality = ""
      var refId = ""
      var latitude = 0
      var longitude = 0
      var quality = 0

      if (location !== undefined) {
        if (location.name !== undefined) {
          name = location.name
          if (location.disassembledName !== undefined) {
            name = location.disassembledName
          }
          if (location.buildingNumber !== undefined) {
            name = name + " " + location.buildingNumber
          }
        }

        if (location.type !== undefined) {
          if (location.type === "suburb") {
            type = SearchLocationType.location
          } else if (location.type === "poi") {
            type = SearchLocationType.poi
          } else if (location.type === "stop") {
            type = SearchLocationType.station
          } else if (location.type === "street" || location.type === "singlehouse") {
            type = SearchLocationType.address
          }
        }

        if (location.parent.name !== undefined) {
          locality = location.parent.name
        }

        if (location.id !== undefined) {
          refId = location.id
        }

        if (location.coord !== undefined) {
          var coordsArray = location.coord

          if (coordsArray.length == 2) {
            latitude = parseFloat(coordsArray[0])
            longitude = parseFloat(coordsArray[1])
          }
        }

        if (location.matchQuality !== undefined) {
          quality = location.matchQuality
        }
      }

      return new SearchLocation(name, locality, type, refId, latitude, longitude, quality)
    }

    return <Context.Provider value={contextObject}>{children}</Context.Provider>
  }

  const useStore = () => useContext<IDataStore>(Context)
  return { Provider, useStore }
}

const DataStore = createStore()

export default DataStore
